
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SidebarProvider, SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";
import DashboardStats from "@/components/dashboard/DashboardStats";
import DashboardContent from "@/components/dashboard/DashboardContent";
import AdminSidebar from "@/components/dashboard/AdminSidebar";
import { dashboardItems } from "@/data/dashboardData";
import { Menu, Clock, CalendarDays } from "lucide-react";

const AdminDashboard = () => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const dayNameAr = currentTime.toLocaleDateString("ar-SA", { weekday: "long" });
  const dayNameEn = currentTime.toLocaleDateString("en-US", { weekday: "long" });
  const dateStr = isRTL
    ? currentTime.toLocaleDateString("ar-SA", { year: "numeric", month: "long", day: "numeric" })
    : currentTime.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const timeStr = currentTime.toLocaleTimeString(isRTL ? "ar-SA" : "en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  return (
    <div className={`min-h-screen relative ${isRTL ? "rtl" : "ltr"}`} style={{ background: 'linear-gradient(135deg, #1a0f22, #241530, #1a0f22)' }}>
      {/* Islamic Geometric Pattern */}
      <div className="fixed inset-0 opacity-5 pointer-events-none">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="adminPattern" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
              <path d="M30 0L60 30L30 60L0 30Z" fill="none" stroke="white" strokeWidth="0.5"/>
              <circle cx="30" cy="30" r="10" fill="none" stroke="white" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#adminPattern)" />
        </svg>
      </div>

      {/* Glowing Orbs */}
      <div className="fixed top-0 left-1/4 w-96 h-96 bg-primary/15 rounded-full blur-3xl pointer-events-none" />
      <div className="fixed bottom-0 right-1/4 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />

      <SidebarProvider defaultOpen={true}>
        <div className="flex min-h-screen w-full">
          <AdminSidebar 
            menuItems={dashboardItems} 
          />
          
          <SidebarInset className="flex-1 bg-transparent">
            <div className="mx-auto w-full h-full overflow-y-auto px-4 md:px-6">
              <div className="max-w-6xl mx-auto py-4 md:py-5">
                <div className="flex items-center justify-between mb-5 md:mb-6">
                  <div className="flex items-center gap-3">
                    <SidebarTrigger className="md:hidden p-2.5 bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg shadow-sm">
                      <Menu className="h-5 w-5" />
                    </SidebarTrigger>
                    <div>
                      <h1 className="text-2xl md:text-3xl font-bold text-white">
                        {isRTL ? "لوحة التحكم الأدارة" : "Admin Dashboard"}
                      </h1>
                      <p className="text-xs text-white/40 mt-1">
                        {isRTL ? "جميع الحقوق محفوظة لـ MDYAF ALARABIA CO 2026" : "All rights reserved to MDYAF ALARABIA CO 2026"}
                      </p>
                      <p className="mt-1 text-white/50 text-sm md:text-base">
                        {isRTL ? "مرحباً بك في لوحة التحكم" : "Welcome to your dashboard"}
                      </p>
                      <div className="flex items-center gap-3 mt-2">
                        <div className="flex items-center gap-1.5 text-primary/80">
                          <CalendarDays className="h-4 w-4" />
                          <span className="text-sm font-medium">{isRTL ? dayNameAr : dayNameEn}</span>
                          <span className="text-white/30 mx-1">|</span>
                          <span className="text-sm text-white/60">{dateStr}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-primary/80">
                          <Clock className="h-4 w-4" />
                          <span className="text-sm font-mono text-white/60">{timeStr}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <DashboardStats 
                  language={i18n.language} 
                  className="animate-fade-in w-full"
                />

                <div className="mt-6 w-full">
                  <DashboardContent 
                    menuItems={dashboardItems} 
                  />
                </div>
              </div>
            </div>
          </SidebarInset>
        </div>
      </SidebarProvider>
    </div>
  );
};

export default AdminDashboard;
